import React, { useRef, useState } from "react";
import styled from "@emotion/styled";
import theme from "theme";
import InnoIcons from "@InnoIcons/";
import { readFromLocalStorage } from "utils/helper";
import axios from "axios";
import TimezoneConverter from "components/TimeZoneConvertor";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  background: ${theme.gradient.themeGradient};
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 20px;
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 30px 20px 30px 40px;
`;

const FileDetailsPrintModal = ({
  openModalView,
  setOpenModalView,
  title = "Document Details",
  fileData,
  pathData,
}) => {
  const modalRef = useRef();
  const [isLoading, setIsLoading] = useState(false);

  const {
    REACT_APP_BASE_URL: baseURL,
    REACT_APP_REQUEST_TIMEOUT: timeout,
    REACT_APP_API_KEY,
  } = process.env;

  const accessToken = REACT_APP_API_KEY;
  const userAuth = readFromLocalStorage("userAccessToken") || "";
  const BASE_URL = baseURL || `${window.location.origin}/api`;

  const handlePrint = async () => {
    try {
      setIsLoading(true);
      const htmlContent = modalRef.current?.outerHTML;
      if (!htmlContent) return alert("No modal content found to print");

      const requestTimeout = timeout ? parseInt(timeout, 10) * 1000 : 180000;
      const url = BASE_URL + "/v1/file/print";

      const response = await axios.post(
        url,
        { html: htmlContent },
        {
          headers: {
            "Content-Type": "application/json",
            api_key: accessToken,
            Authorization: `Bearer ${userAuth}`,
          },
          responseType: "blob",
          timeout: requestTimeout,
        }
      );

      if (response.status === 200) {
        setIsLoading(true);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      } else alert("Failed to generate PDF");
    } catch (error) {
      console.error("PDF preview error:", error);
      alert("An error occurred while generating the PDF");
    } finally {
      setIsLoading(false);
    }
  };

  if (!openModalView) return null;

  const labelValue = (label, value) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          marginBottom: 5,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            color: "#383636ff",
            fontSize: 14,
            flex: 1,
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 14, flex: 3 }}>{value}</div>
      </div>
    );
  };

  return (
    <Overlay>
      <ModalWrapper>
        <ModalHeader>
          <Title>{title}</Title>
          <HeaderButtons>
            <InnoIcons.PrintIcon
              sx={{
                cursor: isLoading ? "not-allowed" : "pointer",
                color: "#fff",
                opacity: isLoading ? 0.5 : 1,
                pointerEvents: isLoading ? "none" : "auto",
              }}
              onClick={!isLoading ? handlePrint : undefined}
            />
            <InnoIcons.CloseIcon
              onClick={() => setOpenModalView(false)}
              style={{ cursor: "pointer", color: "#fff" }}
            />
          </HeaderButtons>
        </ModalHeader>

        <ModalContent
          ref={modalRef}
          style={{
            fontFamily: "'Roboto', sans-serif",
            color: "#464242ff",
          }}
        >
          {/* Breadcrumb with Login link */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: 14,
              color: "#999",
              marginBottom: 5,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <a
                href="https://bimbild.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#0073e6",
                  textDecoration: "none",
                  marginRight: 5,
                }}
              >
                www.bimbild.com
              </a>
              <span style={{ color: "#999" }}>{pathData}</span>
            </div>

            {/* Right-side Login link */}
            <a
              href="https://bimbild.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0073e6",
                fontWeight: 500,
                textDecoration: "none",
              }}
            >
              <img
                alt="ApplicationLogo"
                src={
                  "https://img1.wsimg.com/isteam/ip/fd493636-7eb8-421f-b03a-00e4e93ef2ba/Bimbild1%20(thick).png/:/rs=h:105,cg:true,m/qt=q:95"
                }
                style={{
                  objectFit: "contain",
                  width: "150px",
                  display: "block",
                }}
              />
            </a>
          </div>

          {/* Title */}
          <h1
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: "#383636ff",
              marginBottom: 24,
              borderBottom: "1px solid #e5e5e5",
              paddingBottom: 15,
              paddingTop: 0,
              marginTop: 10,
            }}
          >
            {fileData.subject}
          </h1>

          {/* File Details Two Column */}
          <div
            style={{
              marginBottom: "20px",
            }}
          >
            {labelValue("File Name", fileData?.subject)}
            {labelValue("Uploaded By", fileData?.createdByName)}

            {labelValue("Original File Name", fileData?.fileName)}
            {labelValue(
              "Uploaded Date & Time",
              <TimezoneConverter time={fileData.createdOn} />
            )}
            {labelValue("Type Of Document", fileData?.fileType)}
            {labelValue(
              "Last Updated",
              <TimezoneConverter time={fileData.lastUpdate} />
            )}
            {labelValue("Size", fileData?.fileSize)}
            {labelValue("Revision", fileData?.revisionId)}
          </div>

          {/* Document Summary */}
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "#383636ff",

              marginBottom: 10,
            }}
          >
            Document Summary
          </div>
          <div
            style={{
              fontSize: 14,

              lineHeight: 1.6,
              borderTop: "1px solid #e5e5e5",
              whiteSpace: "pre-wrap",
            }}
            dangerouslySetInnerHTML={{ __html: fileData.note || "" }}
          />
        </ModalContent>
      </ModalWrapper>
    </Overlay>
  );
};

export default FileDetailsPrintModal;
